/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Paginated appointment list fetch helpers for ViewAppointments.
 * @module pages/viewAppointments/viewAppointmentsPageFetch
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { getEventAppointmentsPage } from "../../logic/appointment/index.js";
import { buildAppointmentListParams } from "../../logic/appointments/appointmentListParams.js";
import { normalizeAppointmentsPageResponse } from "../../logic/appointments/appointmentsPageResponse.js";

/**
 * Builds list query params from current filter state.
 * @param {Object} params
 * @returns {Object}
 */
export const buildViewAppointmentsListParams = ({
  page,
  currentPage,
  resultsLimit,
  debouncedSearch,
  selectedRequirement,
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  selectedStatusFilter,
  selectedRequirementsFilter,
}) =>
  buildAppointmentListParams({
    page: page ?? currentPage,
    limit: resultsLimit,
    debouncedSearch,
    selectedRequirement,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    selectedStatusFilter,
    selectedRequirementsFilter,
  });

/**
 * Applies a paginated appointments response to list state setters.
 * @param {Object} pageData
 * @param {Object} setters
 */
export const applyViewAppointmentsPage = (pageData, setters) => {
  const { items, total, page: nextPage } =
    normalizeAppointmentsPageResponse(pageData);
  setters.setFilteredRequests(items);
  setters.setTotalAppointments(total);
  setters.setCurrentPage(nextPage);
};

/**
 * Fetches one page of event appointments with stale-request guarding.
 * @param {Object} params
 * @returns {Promise<void>}
 */
export const fetchViewAppointmentsPage = async ({
  page = 1,
  navigate,
  eventData,
  canLoad,
  listParams,
  requestRef,
  setIsLoading,
  setters,
}) => {
  if (!canLoad()) {
    return;
  }

  const requestId = requestRef.current + 1;
  requestRef.current = requestId;
  setIsLoading(true);

  try {
    const pageData = await getEventAppointmentsPage(
      navigate,
      eventData._id,
      listParams,
    );

    if (requestId !== requestRef.current) {
      return;
    }

    applyViewAppointmentsPage(pageData, setters);
  } catch (error) {
    logger.error("Error fetching paginated appointments:", {
      err: sanitizeError(error),
    });
  } finally {
    if (requestId === requestRef.current) {
      setIsLoading(false);
    }
  }
};
